// Create a deal, or edit one.
//
// The field that isn't here on an edit is the interesting one. `currency` is
// immutable (`inputs.ts`, D8): every term line and invoice line under a
// deal is implicitly denominated in the DEAL's currency — there is no per-line
// currency column — so flipping it would reinterpret $6,120.00 CAD of frozen
// billing history as USD without touching a single stored number. A deal in the
// wrong currency is re-created, not edited. It is shown, disabled, with that
// reason attached, because a field that simply vanished would read as a bug.
//
// `partner_id` and `agreement_id` DO stay editable: moving a deal to another
// agreement is ordinary commercial reality, and a trigger still forces the pair
// to be consistent — the agreement must be the partner's own or one of its
// owners' — which is why choosing a partner resets the agreement rather than
// leaving a pair on screen that the new partner may have no claim to.
//
// The agreement is OPTIONAL, and blank is the normal case: it means the deal
// inherits its partner's terms (or the nearest owner's above it) instead of
// pinning terms of its own. Since blank is a real answer rather than an
// unfinished one, the form says what it will resolve to before the deal exists
// — including the case where nothing resolves, which is a deal every report
// will later refuse with `split_unresolvable`.

import type { ReactNode } from "react";
import {
  Currency,
  customerLocationProblem,
  DealCreate,
  DealState,
  DealUpdate,
  InvoiceRecipient,
} from "@crm/shared";
import type { Agreement, Deal, Partner } from "@crm/shared";
import { useDealMutations } from "../../api/mutations.js";
import { Badge } from "../../design/Badge.js";
import { usePartnerOptions, useAvailableAgreements } from "../../api/queries.js";
import { inheritedAgreement } from "../partners/partnerTree.js";
import { ProblemMessage, Spinner } from "../../design/Feedback.js";
import { FormDialog } from "../../forms/FormDialog.js";
import {
  DateField,
  NumberField,
  ReadOnlyField,
  SelectField,
  TextField,
  type SelectOption,
} from "../../forms/fields.js";
import type { FieldErrors } from "../../forms/formErrors.js";
import {
  optionalDate,
  optionalText,
  parseDecimal,
  parseOptionalDecimal,
  parseOptionalInteger,
  textOf,
} from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { countryOptionsFor } from "../../format/countries.js";
import { formatMoney, formatPercent, minorToInput, parseMoneyToMinor } from "../../format/money.js";
import { dealStateLabel, invoiceRecipientLabel } from "../../format/labels.js";

interface DealDraft {
  partner_id: string;
  agreement_id: string;
  customer_name: string;
  currency: string;
  state: string;
  win_prob: string;
  start_date_earliest: string;
  start_date_latest: string;
  contract_term_months: string;
  renewal_date: string;
  /** Blank means "not closed" — null. The server stamps it when the state closes. */
  closed_on: string;
  invoice_recipient: string;
  /** Blank means "the agreement decides" — a null, which is the normal case. */
  split_override_pct: string;
  customer_account_count: string;
  customer_country: string;
  customer_region: string;
  customer_city: string;
  customer_lat: string;
  customer_lng: string;
  notes: string;
  /** Major units as typed, in the deal's currency. Blank means no estimate — null. */
  estimated_implementation_minor: string;
  estimated_recurring_monthly_minor: string;
  /** Blank means "let the projection schedule it" — null, the usual case. */
  estimated_go_live: string;
}

const LABELS = {
  partner_id: "Partner",
  agreement_id: "Agreement",
  customer_name: "Customer",
  currency: "Currency",
  state: "State",
  win_prob: "Win probability",
  start_date_earliest: "Start date — earliest",
  start_date_latest: "Start date — latest",
  contract_term_months: "Contract term",
  renewal_date: "Renewal date",
  closed_on: "Closed on",
  invoice_recipient: "Invoice recipient",
  split_override_pct: "Our share of this deal",
  customer_account_count: "Customer accounts",
  customer_country: "Country",
  customer_region: "Region or state",
  customer_city: "City",
  customer_lat: "Latitude",
  customer_lng: "Longitude",
  notes: "Notes",
  estimated_implementation_minor: "Estimated implementation fee",
  estimated_recurring_monthly_minor: "Estimated monthly recurring",
  estimated_go_live: "Estimated go-live",
} as const;

const BLANK: DealDraft = {
  partner_id: "",
  agreement_id: "",
  customer_name: "",
  currency: "CAD",
  state: "lead",
  win_prob: "",
  start_date_earliest: "",
  start_date_latest: "",
  contract_term_months: "",
  renewal_date: "",
  closed_on: "",
  invoice_recipient: "partner",
  split_override_pct: "",
  customer_account_count: "",
  customer_country: "",
  customer_region: "",
  customer_city: "",
  customer_lat: "",
  customer_lng: "",
  notes: "",
  estimated_implementation_minor: "",
  estimated_recurring_monthly_minor: "",
  estimated_go_live: "",
};

function draftFromRecord(deal: Deal): DealDraft {
  return {
    partner_id: deal.partner_id,
    // A select holds strings, so "inherit" is the empty option — `toWire` turns
    // it back into the null the contract means by it.
    agreement_id: deal.agreement_id ?? "",
    customer_name: deal.customer_name,
    currency: deal.currency,
    state: deal.state,
    win_prob: textOf(deal.win_prob),
    start_date_earliest: deal.start_date_earliest,
    start_date_latest: deal.start_date_latest,
    contract_term_months: textOf(deal.contract_term_months),
    renewal_date: deal.renewal_date ?? "",
    closed_on: deal.closed_on ?? "",
    invoice_recipient: deal.invoice_recipient,
    split_override_pct: textOf(deal.split_override_pct),
    customer_account_count: textOf(deal.customer_account_count),
    customer_country: deal.customer_country ?? "",
    customer_region: deal.customer_region ?? "",
    customer_city: deal.customer_city ?? "",
    customer_lat: textOf(deal.customer_lat),
    customer_lng: textOf(deal.customer_lng),
    notes: deal.notes ?? "",
    estimated_implementation_minor:
      deal.estimated_implementation_minor === null
        ? ""
        : minorToInput(deal.estimated_implementation_minor, deal.currency),
    estimated_recurring_monthly_minor:
      deal.estimated_recurring_monthly_minor === null
        ? ""
        : minorToInput(deal.estimated_recurring_monthly_minor, deal.currency),
    estimated_go_live: deal.estimated_go_live ?? "",
  };
}

/** The draft's currency, for parsing its amounts. A new deal's select always holds one. */
function draftCurrency(draft: DealDraft): Currency {
  return Currency.safeParse(draft.currency).data ?? "CAD";
}

/**
 * An optional estimate amount: blank is null ("nobody has estimated this"),
 * NOT zero, which would claim the deal is worth nothing. Parsed to minor units
 * with integer arithmetic; anything unparseable is NaN, which the shared schema
 * then refuses on this field.
 */
function optionalMoney(input: string, currency: Currency): number | null {
  return input.trim() === "" ? null : parseMoneyToMinor(input, currency);
}

function toWire(draft: DealDraft): Wire {
  return {
    partner_id: draft.partner_id,
    agreement_id: draft.agreement_id === "" ? null : draft.agreement_id,
    customer_name: draft.customer_name.trim(),
    currency: draft.currency,
    state: draft.state,
    win_prob: parseDecimal(draft.win_prob),
    start_date_earliest: draft.start_date_earliest,
    start_date_latest: draft.start_date_latest,
    contract_term_months: parseOptionalInteger(draft.contract_term_months),
    renewal_date: optionalDate(draft.renewal_date),
    closed_on: optionalDate(draft.closed_on),
    invoice_recipient: draft.invoice_recipient,
    // Blank is null — "the agreement decides" — and NOT zero, which would mean
    // we keep nothing. `useEditor` only sends a key that differs from the
    // stored row, so clearing an already-empty box sends nothing at all.
    split_override_pct: parseOptionalDecimal(draft.split_override_pct),
    // Blank is null — "nobody has sized this customer yet" — and NOT zero. The
    // implementation-fee report puts a null in its own bucket; a 0 would price
    // the deal in the cheapest band as though somebody had measured it.
    customer_account_count: parseOptionalInteger(draft.customer_account_count),
    // Blank is `null` — "clear this" — not `""`, which the contract would
    // reject as a country that is not two letters and a region that is empty.
    customer_country: optionalText(draft.customer_country),
    customer_region: optionalText(draft.customer_region),
    customer_city: optionalText(draft.customer_city),
    customer_lat: parseOptionalDecimal(draft.customer_lat),
    customer_lng: parseOptionalDecimal(draft.customer_lng),
    notes: optionalText(draft.notes),
    // ESTIMATES, not contract terms: the projection uses them only while the
    // deal has no revenue term line. Gross, before any partner share.
    estimated_implementation_minor: optionalMoney(
      draft.estimated_implementation_minor,
      draftCurrency(draft),
    ),
    estimated_recurring_monthly_minor: optionalMoney(
      draft.estimated_recurring_monthly_minor,
      draftCurrency(draft),
    ),
    estimated_go_live: optionalDate(draft.estimated_go_live),
  };
}

const COORDINATES_TOGETHER =
  "Latitude and longitude go in together, or neither does — half a coordinate cannot be plotted.";

const NOT_A_NUMBER = "Enter a decimal degree, e.g. 51.0447. Nothing else is a coordinate.";

/**
 * The two location rules, applied HERE rather than left to the round trip.
 *
 * The country rule comes from `customerLocationProblem` in the contract — the
 * same function the API applies to the row a PATCH will produce — because this
 * form always holds the whole resulting deal, so "the resulting row" is exactly
 * what is on screen. Restating the rule here as a second sentence is how the
 * client and the server end up disagreeing about which one they meant.
 *
 * The coordinate rule is checked on the DRAFT, before parsing, so a blank field
 * beside a filled one is reported as the pair being half-supplied rather than
 * as `NaN` failing a number schema.
 */
function validateLocation(draft: DealDraft): FieldErrors {
  const errors: Record<string, string> = {};

  const lat = draft.customer_lat.trim();
  const lng = draft.customer_lng.trim();
  if (lat === "" && lng !== "") errors["customer_lat"] = COORDINATES_TOGETHER;
  if (lng === "" && lat !== "") errors["customer_lng"] = COORDINATES_TOGETHER;
  if (lat !== "" && Number.isNaN(parseDecimal(lat))) errors["customer_lat"] = NOT_A_NUMBER;
  if (lng !== "" && Number.isNaN(parseDecimal(lng))) errors["customer_lng"] = NOT_A_NUMBER;

  const problem = customerLocationProblem({
    customer_country: optionalText(draft.customer_country),
    customer_region: optionalText(draft.customer_region),
    customer_city: optionalText(draft.customer_city),
  });
  if (problem !== null) errors["customer_country"] = problem;

  return errors;
}

const ESTIMATE_MONEY_FIELDS: ReadonlySet<string> = new Set([
  "estimated_implementation_minor",
  "estimated_recurring_monthly_minor",
]);

function formatValueIn(currency: Currency): (field: string, value: unknown) => string {
  return (field, value) => {
    if (value === null || value === undefined) return "—";
    if (field === "win_prob" && typeof value === "number") return formatPercent(value);
    if (ESTIMATE_MONEY_FIELDS.has(field) && typeof value === "number") {
      return formatMoney(value, currency);
    }
    return String(value);
  };
}

/** What every estimate field says under it: it is an estimate, and what for. */
function EstimateHint({ children }: { children?: ReactNode }): ReactNode {
  return (
    <>
      <Badge shape="tag" tone="warn">
        Estimate
      </Badge>{" "}
      Used only for projections until the deal has contract terms.
      {children === undefined ? null : <> {children}</>}
    </>
  );
}

const STATE_OPTIONS: readonly SelectOption<DealState>[] = DealState.options.map((state) => ({
  value: state,
  label: dealStateLabel(state),
}));

const RECIPIENT_OPTIONS: readonly SelectOption<InvoiceRecipient>[] = InvoiceRecipient.options.map(
  (recipient) => ({ value: recipient, label: invoiceRecipientLabel(recipient) }),
);

const CURRENCY_OPTIONS: readonly SelectOption<Currency>[] = Currency.options.map((currency) => ({
  value: currency,
  label: currency,
}));

function partnerNameOf(partners: readonly Partner[], partnerId: string): string {
  return partners.find((partner) => partner.id === partnerId)?.name ?? "an owner above";
}

/**
 * The agreements this deal may pin. An agreement belonging to a partner OTHER
 * than the deal's own is one inherited from an owner, and saying whose paper it
 * is on is the only thing that distinguishes "Harris master 2026" offered under
 * Cayenta from an agreement Cayenta signed itself.
 */
function agreementOptions(
  agreements: readonly Agreement[],
  partners: readonly Partner[],
  partnerId: string,
): readonly SelectOption<string>[] {
  return agreements.map((agreement) => ({
    value: agreement.id,
    label:
      agreement.partner_id === partnerId
        ? agreement.name
        : `${agreement.name} — from ${partnerNameOf(partners, agreement.partner_id)}`,
  }));
}

interface AgreementHintInput {
  partnerId: string;
  agreementId: string;
  agreements: readonly Agreement[];
  partners: readonly Partner[];
  isPending: boolean;
}

/**
 * What the current choice MEANS, in the two cases where the control alone does
 * not say it: a blank that will inherit something, and a blank that will
 * inherit nothing. The second one is the reason this exists — a deal whose
 * split cannot resolve looks exactly like a deal happily inheriting until a
 * report refuses to run over it.
 *
 * The resolution is predicted from the ownership chain and the same
 * `available_to_partner_id` list the picker is drawn from; the API remains the
 * authority and publishes its own answer as `effective_agreement_id` once the
 * deal exists.
 */
function agreementHint({
  partnerId,
  agreementId,
  agreements,
  partners,
  isPending,
}: AgreementHintInput): string {
  if (partnerId === "") {
    return "Choose a partner first — what may be pinned here is its own agreements and its owners'.";
  }

  if (agreementId !== "") {
    const pinned = agreements.find((agreement) => agreement.id === agreementId);
    if (pinned !== undefined && pinned.partner_id !== partnerId) {
      return `Pinned to ${partnerNameOf(partners, pinned.partner_id)}'s agreement. This deal resolves its split there whatever its own partner falls back to.`;
    }
    return "Pinned: this deal's split resolves through this agreement's term timeline, overriding whatever its partner would otherwise fall back to.";
  }

  if (isPending) return "Working out what this deal would inherit…";

  const inherited = inheritedAgreement(agreements, partners, partnerId);
  if (inherited === null) {
    return "Nothing here or above it has a default agreement, so this deal's split would not resolve — every report touching it fails with split_unresolvable until one does.";
  }
  return inherited.partner_id === partnerId
    ? `Inherits ${inherited.name}, this partner's default agreement.`
    : `Inherits ${inherited.name}, on ${partnerNameOf(partners, inherited.partner_id)}'s paper.`;
}

export interface DealFormDialogProps {
  /** Absent for a new deal. */
  deal?: Deal;
  onClose: () => void;
  onSaved?: (deal: Deal) => void;
}

export function DealFormDialog({ deal, onClose, onSaved }: DealFormDialogProps): ReactNode {
  const mutations = useDealMutations();
  const isNew = deal === undefined;

  const editor = useEditor<DealDraft, DealCreate, DealUpdate, Deal>({
    mode: deal === undefined ? { kind: "create" } : { kind: "update", record: deal },
    blankDraft: BLANK,
    draftFromRecord,
    toWire,
    createSchema: DealCreate,
    updateSchema: DealUpdate,
    validateDraft: validateLocation,
    // `DealUpdate` refuses a body carrying one coordinate without the other, so
    // nudging a latitude has to send the longitude too — otherwise the
    // diff-based PATCH would build a body the shared schema itself rejects.
    coupledFields: [["customer_lat", "customer_lng"]],
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: (saved) => {
      onSaved?.(saved);
      onClose();
    },
  });

  const partners = usePartnerOptions();
  // Not `useAgreements`: a deal may pin an agreement belonging to any partner
  // ABOVE its own, and a list filtered to the partner's own would hide exactly
  // the owner's master agreement most of these deals run on.
  const agreements = useAvailableAgreements(editor.draft.partner_id);

  const allPartners = partners.data?.items ?? [];
  const available = agreements.data?.items ?? [];

  const partnerOptions: readonly SelectOption<string>[] = allPartners.map((partner) => ({
    value: partner.id,
    label: partner.name,
  }));

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNew ? "New deal" : "Edit deal"}
      submitLabel={isNew ? "Create deal" : "Save changes"}
      editor={editor}
      labels={LABELS}
      formatValue={formatValueIn(draftCurrency(editor.draft))}
    >
      {partners.error !== null && (
        <ProblemMessage error={partners.error} action="load the partner list" />
      )}
      {partners.isPending && <Spinner label="Loading partners…" inline />}

      <SelectField
        label={LABELS.partner_id}
        required
        placeholder="Choose a partner"
        value={editor.draft.partner_id}
        options={partnerOptions}
        error={editor.errors["partner_id"]}
        onChange={(value) => {
          editor.setField("partner_id", value);
          // A pinned agreement must belong to the new partner or to one of its
          // owners, and the database refuses anything else with
          // `reference_not_found`. The previous partner's paper is rarely in
          // that set, so the selection is cleared rather than carried across —
          // blank is a real answer here (inherit), not an empty field.
          editor.setField("agreement_id", "");
        }}
      />

      <SelectField
        label={LABELS.agreement_id}
        placeholder={
          editor.draft.partner_id === ""
            ? "Choose a partner first"
            : agreements.isPending
              ? "Loading agreements…"
              : "Inherit from partner"
        }
        disabled={editor.draft.partner_id === ""}
        value={editor.draft.agreement_id}
        options={agreementOptions(available, allPartners, editor.draft.partner_id)}
        error={editor.errors["agreement_id"]}
        hint={agreementHint({
          partnerId: editor.draft.partner_id,
          agreementId: editor.draft.agreement_id,
          agreements: available,
          partners: allPartners,
          isPending: agreements.isPending,
        })}
        onChange={(value) => editor.setField("agreement_id", value)}
      />

      <TextField
        label={LABELS.customer_name}
        required
        value={editor.draft.customer_name}
        error={editor.errors["customer_name"]}
        onChange={(value) => editor.setField("customer_name", value)}
      />

      {isNew ? (
        <SelectField
          label={LABELS.currency}
          required
          value={editor.draft.currency}
          options={CURRENCY_OPTIONS}
          error={editor.errors["currency"]}
          hint="Every term line under this deal, and every figure its clauses bill, is in this currency. It cannot be changed afterwards."
          onChange={(value) => editor.setField("currency", value)}
        />
      ) : (
        <ReadOnlyField
          label={LABELS.currency}
          value={editor.draft.currency}
          reason="A deal's currency is fixed. Its term lines and its billing history carry no currency of their own — they are all in this one — so a change here would silently restate every amount underneath. Re-create the deal instead."
        />
      )}

      <SelectField
        label={LABELS.state}
        required
        value={editor.draft.state}
        options={STATE_OPTIONS}
        error={editor.errors["state"]}
        onChange={(value) => editor.setField("state", value)}
      />

      <NumberField
        label={LABELS.win_prob}
        required
        suffix="%"
        hint="Weights this deal's value in the pipeline report."
        value={editor.draft.win_prob}
        error={editor.errors["win_prob"]}
        onChange={(value) => editor.setField("win_prob", value)}
      />

      <DateField
        label={LABELS.start_date_earliest}
        required
        value={editor.draft.start_date_earliest}
        error={editor.errors["start_date_earliest"]}
        onChange={(value) => editor.setField("start_date_earliest", value)}
      />
      <DateField
        label={LABELS.start_date_latest}
        required
        hint="Same as the earliest date means a firm start, which lands wholly in one pipeline month. A later date makes it a range, and the pipeline spreads the weighted value across every month it touches."
        value={editor.draft.start_date_latest}
        error={editor.errors["start_date_latest"]}
        onChange={(value) => editor.setField("start_date_latest", value)}
      />

      <NumberField
        label={LABELS.contract_term_months}
        suffix="months"
        hint="Optional."
        value={editor.draft.contract_term_months}
        error={editor.errors["contract_term_months"]}
        onChange={(value) => editor.setField("contract_term_months", value)}
      />
      <DateField
        label={LABELS.renewal_date}
        value={editor.draft.renewal_date}
        error={editor.errors["renewal_date"]}
        onChange={(value) => editor.setField("renewal_date", value)}
      />
      <DateField
        label={LABELS.closed_on}
        hint="The day the deal was won or lost. Filled in when the state closes; set it to the real signing date if that was a different day. Cleared when a deal reopens."
        value={editor.draft.closed_on}
        error={editor.errors["closed_on"]}
        onChange={(value) => editor.setField("closed_on", value)}
      />

      <SelectField
        label={LABELS.invoice_recipient}
        required
        value={editor.draft.invoice_recipient}
        options={RECIPIENT_OPTIONS}
        error={editor.errors["invoice_recipient"]}
        onChange={(value) => editor.setField("invoice_recipient", value)}
      />

      {/* The deal-level split (migration 0014). Sits next to the invoice
          recipient because the two answer adjacent questions — who gets billed,
          and how much of that we keep — and because both are commercial
          positions rather than facts about the customer. */}
      <NumberField
        label={LABELS.split_override_pct}
        suffix="%"
        placeholder="From the agreement"
        hint="Leave blank — the normal case — and every clause under this deal takes its split from the governing agreement's term in force on the period it bills. Set 100 for an engagement the partner takes no revenue share on: we invoice the fee and keep all of it. Anything here beats the agreement and is beaten by a term line's own override, and it re-prices only what has NOT been invoiced yet."
        value={editor.draft.split_override_pct}
        error={editor.errors["split_override_pct"]}
        onChange={(value) => editor.setField("split_override_pct", value)}
      />

      {/* How big the end customer is. Placed with the commercial fields rather
          than with the location ones because it is a commercial input: it
          selects the band in the agreement's implementation-fee schedule, and
          that is the number the deal's buy-in fee comes from. Optional, and
          blank stays blank — see the deal screen's implementation-fee card for
          what a missing count costs. */}
      <NumberField
        label={LABELS.customer_account_count}
        hint="The end customer's own customer-account count. It selects the band in the governing agreement's implementation-fee schedule — leave blank if nobody has sized them yet, which is honest; a guess here moves real money."
        value={editor.draft.customer_account_count}
        error={editor.errors["customer_account_count"]}
        onChange={(value) => editor.setField("customer_account_count", value)}
      />

      {/* ESTIMATES. What the deal is expected to be worth before anybody has
          signed terms, for the projection report only. Grouped and badged so
          they never read as the contract: once the deal has a revenue term
          line, the projection ignores them. Gross, before the partner share,
          in the deal's currency. */}
      <NumberField
        label={LABELS.estimated_implementation_minor}
        suffix={editor.draft.currency}
        placeholder="Not estimated"
        hint={<EstimateHint>One-time, gross, before any partner share.</EstimateHint>}
        value={editor.draft.estimated_implementation_minor}
        error={editor.errors["estimated_implementation_minor"]}
        onChange={(value) => editor.setField("estimated_implementation_minor", value)}
      />
      <NumberField
        label={LABELS.estimated_recurring_monthly_minor}
        suffix={`${editor.draft.currency} / month`}
        placeholder="Not estimated"
        hint={<EstimateHint>Per month once live, gross, before any partner share.</EstimateHint>}
        value={editor.draft.estimated_recurring_monthly_minor}
        error={editor.errors["estimated_recurring_monthly_minor"]}
        onChange={(value) => editor.setField("estimated_recurring_monthly_minor", value)}
      />
      <DateField
        label={LABELS.estimated_go_live}
        hint={
          <EstimateHint>
            Usually left blank: the projection then schedules it by go-live capacity.
          </EstimateHint>
        }
        value={editor.draft.estimated_go_live}
        error={editor.errors["estimated_go_live"]}
        onChange={(value) => editor.setField("estimated_go_live", value)}
      />

      {/* Where the end customer is. Last, before the notes, because it is
          progressive enrichment: a deal is recorded when it is sold and placed
          on the map afterwards, so these five fields should not stand between
          somebody and the commercial ones above. Every one of them is optional
          — a deal with no location is unannotated, not wrong. */}
      <SelectField
        label={LABELS.customer_country}
        placeholder="No country recorded"
        value={editor.draft.customer_country}
        options={countryOptionsFor(editor.draft.customer_country)}
        error={editor.errors["customer_country"]}
        hint="Stored as the ISO-3166-1 alpha-2 code, which is what the revenue-by-region report groups on. Required once a region or a city is filled in."
        onChange={(value) => editor.setField("customer_country", value)}
      />

      <TextField
        label={LABELS.customer_region}
        placeholder="Alberta, or CA-AB"
        hint="Free text — whichever spelling your records use. It only has to be consistent with itself to roll up."
        value={editor.draft.customer_region}
        error={editor.errors["customer_region"]}
        onChange={(value) => editor.setField("customer_region", value)}
      />

      <TextField
        label={LABELS.customer_city}
        value={editor.draft.customer_city}
        error={editor.errors["customer_city"]}
        onChange={(value) => editor.setField("customer_city", value)}
      />

      <NumberField
        label={LABELS.customer_lat}
        suffix="°N"
        placeholder="51.0447"
        hint="Optional, and entered by hand — nothing here geocodes. Set both coordinates or neither; they are what a map draws a pin from."
        value={editor.draft.customer_lat}
        error={editor.errors["customer_lat"]}
        onChange={(value) => editor.setField("customer_lat", value)}
      />

      <NumberField
        label={LABELS.customer_lng}
        suffix="°E"
        placeholder="-114.0719"
        value={editor.draft.customer_lng}
        error={editor.errors["customer_lng"]}
        onChange={(value) => editor.setField("customer_lng", value)}
      />

      <TextField
        label={LABELS.notes}
        multiline
        value={editor.draft.notes}
        error={editor.errors["notes"]}
        onChange={(value) => editor.setField("notes", value)}
      />
    </FormDialog>
  );
}
